import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button } from "@project/ui";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { ApiRequestError } from "@apps/dashboard/lib/api-fetch";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import {
  fetchFinancialProfile,
  homeExpenseQueryKeys,
  patchFinancialProfile,
} from "@apps/dashboard/lib/home-expense-api";
import type { HouseListItem } from "@apps/dashboard/lib/home-expense-types";
import { minorToMajorInputValue, parseAmountToMinor } from "@apps/dashboard/lib/parse-amount";
import { dashboardPaths } from "@apps/dashboard/lib/routes";

export interface HouseFinancialProfileSectionProps {
  houseId: string;
  role: HouseListItem["role"];
  /** El botón «Editar» está en la barra derecha de la tarjeta (Mis casas). */
  isEditing: boolean;
  onClose: () => void;
}

export function HouseFinancialProfileSection({
  houseId,
  role,
  isEditing,
  onClose,
}: HouseFinancialProfileSectionProps): JSX.Element {
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();

  const [draftBalance, setDraftBalance] = useState("");
  const [draftCurrency, setDraftCurrency] = useState("");
  const [draftCap, setDraftCap] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const { data: profile, isLoading, isError } = useQuery({
    queryKey: homeExpenseQueryKeys.financialProfile(locale, houseId),
    queryFn: () => fetchFinancialProfile(locale, houseId),
  });

  useEffect(() => {
    if (isEditing && profile) {
      setDraftBalance(minorToMajorInputValue(profile.openingBankBalanceMinor));
      setDraftCurrency((profile.currency ?? "EUR").trim().toUpperCase());
      setDraftCap(
        profile.monthlyBudgetCapMinor != null ? minorToMajorInputValue(profile.monthlyBudgetCapMinor) : "",
      );
      setLocalError(null);
    }
  }, [isEditing, profile?.houseId, profile?.openingBankBalanceMinor, profile?.currency, profile?.monthlyBudgetCapMinor]);

  const mutation = useMutation({
    mutationFn: (body: {
      openingBankBalanceMinor: number;
      currency: string;
      monthlyBudgetCapMinor: number | null;
    }) => patchFinancialProfile(locale, houseId, body),
    onSuccess: () => {
      onClose();
      setLocalError(null);
      toast.success(t("dashboardUi.houses.financialProfileSaveSuccess"));
      void queryClient.invalidateQueries({ queryKey: homeExpenseQueryKeys.financialProfile(locale, houseId) });
      void queryClient.invalidateQueries({ queryKey: ["houses", houseId, "dashboard", "summary"] });
    },
    onError: (err: unknown) => {
      const msg = err instanceof ApiRequestError ? err.message : t("dashboardUi.auth.errors.generic");
      setLocalError(msg);
    },
  });

  function onSubmit(e: React.FormEvent): void {
    e.preventDefault();
    setLocalError(null);
    const balanceMinor = parseAmountToMinor(draftBalance);
    if (balanceMinor === null) {
      setLocalError(t("dashboardUi.houses.openingBalanceInvalidAmount"));
      return;
    }
    const cur = draftCurrency.trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(cur)) {
      setLocalError(t("dashboardUi.houses.financialProfileInvalidCurrency"));
      return;
    }
    const capTrim = draftCap.trim();
    let monthlyBudgetCapMinor: number | null;
    if (capTrim === "") {
      monthlyBudgetCapMinor = null;
    } else {
      const capMinor = parseAmountToMinor(draftCap);
      if (capMinor === null) {
        setLocalError(t("dashboardUi.houses.monthlyCapInvalidAmount"));
        return;
      }
      monthlyBudgetCapMinor = capMinor;
    }
    mutation.mutate({
      openingBankBalanceMinor: balanceMinor,
      currency: cur,
      monthlyBudgetCapMinor,
    });
  }

  if (isLoading) {
    return (
      <div className="mt-3 border-t border-outline-variant/15 pt-3" aria-busy>
        <div className="h-3 w-48 animate-pulse rounded bg-on-surface-variant/15" />
      </div>
    );
  }

  if (isError || !profile) {
    return (
      <div className="mt-3 border-t border-outline-variant/15 pt-3">
        <p className="text-body-sm text-on-surface-variant">{t("dashboardUi.houses.financialProfileLoadError")}</p>
      </div>
    );
  }

  const formattedBalance = formatMinorAsCurrency(profile.openingBankBalanceMinor, profile.currency, locale);
  const capMinor = profile.monthlyBudgetCapMinor;
  const formattedCap =
    capMinor != null ? formatMinorAsCurrency(capMinor, profile.currency, locale) : null;

  return (
    <div className="mt-3 w-full border-t border-outline-variant/15 pt-3">
      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[10px] font-label uppercase tracking-widest text-on-surface-variant/80">
            {t("dashboardUi.houses.financialProfileSectionTitle")}
          </p>
          <p className="mt-1 max-w-xl font-body text-xs text-on-surface-variant">
            {t("dashboardUi.houses.financialProfileSubtitle")}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" className="w-full shrink-0 sm:w-auto" asChild>
          <Link to={dashboardPaths.houseFinancings(houseId)}>{t("dashboardUi.houses.financialProfileFinancingsLink")}</Link>
        </Button>
      </div>

      {!isEditing ? (
        <div className="space-y-3">
          <div>
            <p className="text-[10px] font-label uppercase tracking-widest text-on-surface-variant/80">
              {t("dashboardUi.houses.openingBalanceLabel")}
            </p>
            <p className="mt-1 font-body text-body-sm font-semibold tabular-nums text-on-surface">{formattedBalance}</p>
          </div>
          <div>
            <p className="text-[10px] font-label uppercase tracking-widest text-on-surface-variant/80">
              {t("dashboardUi.houses.currencyLabel")}
            </p>
            <p className="mt-1 font-body text-body-sm font-semibold text-on-surface">{profile.currency}</p>
          </div>
          <div>
            <p className="text-[10px] font-label uppercase tracking-widest text-on-surface-variant/80">
              {t("dashboardUi.houses.monthlyCapLabel")}
            </p>
            <p className="mt-1 font-body text-body-sm font-semibold tabular-nums text-on-surface">
              {formattedCap ?? t("dashboardUi.houses.monthlyCapNone")}
            </p>
          </div>
          {role !== "owner" && role !== "admin" ? (
            <p className="font-body text-xs text-on-surface-variant">{t("dashboardUi.houses.financialProfileReadOnlyHint")}</p>
          ) : null}
        </div>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit}>
          {localError ? (
            <p className="text-body-sm text-error" role="alert">
              {localError}
            </p>
          ) : null}

          <div>
            <label htmlFor={`fp-balance-${houseId}`} className="text-label-md text-on-surface-variant">
              {t("dashboardUi.houses.openingBalanceAmountLabel")}
            </label>
            <input
              id={`fp-balance-${houseId}`}
              inputMode="decimal"
              autoComplete="off"
              value={draftBalance}
              onChange={(e) => setDraftBalance(e.target.value)}
              className="mt-1 w-full max-w-xs rounded-xl border border-outline-variant/30 bg-surface px-4 py-2.5 font-body text-body-md text-on-surface outline-none focus:border-primary"
            />
            <p className="mt-1 max-w-md font-body text-xs text-on-surface-variant">{t("dashboardUi.houses.openingBalanceHint")}</p>
          </div>

          <div>
            <label htmlFor={`fp-currency-${houseId}`} className="text-label-md text-on-surface-variant">
              {t("dashboardUi.houses.currencyLabel")}
            </label>
            <input
              id={`fp-currency-${houseId}`}
              value={draftCurrency}
              onChange={(e) => setDraftCurrency(e.target.value.toUpperCase())}
              maxLength={3}
              autoComplete="off"
              spellCheck={false}
              className="mt-1 w-full max-w-[8rem] rounded-xl border border-outline-variant/30 bg-surface px-4 py-2.5 font-body text-body-md uppercase tabular-nums tracking-wide text-on-surface outline-none focus:border-primary"
            />
            <p className="mt-1 max-w-md font-body text-xs text-on-surface-variant">{t("dashboardUi.houses.currencyPlaceholder")}</p>
          </div>

          <div>
            <label htmlFor={`fp-cap-${houseId}`} className="text-label-md text-on-surface-variant">
              {t("dashboardUi.houses.monthlyCapLabel")}
            </label>
            <input
              id={`fp-cap-${houseId}`}
              inputMode="decimal"
              autoComplete="off"
              value={draftCap}
              onChange={(e) => setDraftCap(e.target.value)}
              placeholder={t("dashboardUi.houses.monthlyCapPlaceholder")}
              className="mt-1 w-full max-w-xs rounded-xl border border-outline-variant/30 bg-surface px-4 py-2.5 font-body text-body-md text-on-surface outline-none focus:border-primary"
            />
            <p className="mt-1 max-w-md font-body text-xs text-on-surface-variant">{t("dashboardUi.houses.monthlyCapHint")}</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" disabled={mutation.isPending}>
              {mutation.isPending ? t("dashboardUi.houses.openingBalanceSaving") : t("dashboardUi.houses.openingBalanceSave")}
            </Button>
            <Button type="button" variant="outline" size="sm" disabled={mutation.isPending} onClick={onClose}>
              {t("dashboardUi.houses.openingBalanceCancel")}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
